import React, { useMemo, useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faTimes,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { JsonForms } from "@jsonforms/react";
import {
  materialRenderers,
  readonlyRenderers,
} from "../../../jsonForms/materialRenderers.jsx";
import { materialCells } from "@jsonforms/material-renderers";

const AppointmentTimelineItem = ({ entry, canEdit, onSave }) => {
  const { t, i18n } = useTranslation(["translation", "serviceTemplates"]);
  const [isEditing, setIsEditing] = useState(false);
  const initFormData = useMemo(
    () =>
      Object.fromEntries(
        entry?.filled_data?.fields?.map((f) => [f.key, f.value]) ?? [],
      ),
    [entry.filled_data?.fields],
  );
  const [formData, setFormData] = useState(initFormData);

  const parsedSchema = useMemo(() => {
    try {
      return JSON.parse(entry.template?.schema || "{}");
    } catch (error) {
      console.error("Invalid JSON schema in entry.template.schema:", error);
      return {};
    }
  }, [entry.template?.schema]);

  const parsedUiSchema = useMemo(() => {
    try {
      return JSON.parse(entry.template?.uischema || "{}");
    } catch (error) {
      console.error("Invalid JSON uischema in entry.template.uischema:", error);
      return {};
    }
  }, [entry.template?.uischema]);

  const translation = useMemo(
    () => (key, defaultMessage) =>
      i18n.exists(`serviceTemplates:${key}`)
        ? t(`serviceTemplates:${key}`)
        : defaultMessage,
    [i18n.language],
  );

  const handleCancel = () => {
    setFormData(initFormData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (onSave) {
      const fields = Object.entries(formData).map(([key, value]) => {
        const schemaProps = parsedSchema?.properties?.[key] || {};
        return {
          key,
          label: schemaProps.title || key,
          value,
          isMedicalDocumentation: schemaProps?.medicalDocumentation === "yes",
        };
      });

      const newData = {
        ...entry.filled_data,
        fields,
      };

      await onSave(newData);
    }
    setIsEditing(false);
  };

  if (!entry?.filled_data?.fields) {
    return (
      <Typography variant="body1">
        {t("serviceTemplates:noTemplateAvailable")}
      </Typography>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {!isEditing && entry.isEditable && canEdit && (
        <Box sx={{ position: "absolute", top: 0, right: "-1rem", zIndex: 2 }}>
          <Tooltip title={t("form.edit", "Edit")}>
            <IconButton
              onClick={() => setIsEditing(true)}
              sx={{ color: "primary.main" }}
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={isEditing ? {} : { "--mui-spacing": "12px" }}>
        <JsonForms
          schema={parsedSchema}
          uischema={parsedUiSchema}
          data={formData}
          renderers={[
            ...materialRenderers,
            ...(isEditing ? [] : readonlyRenderers),
          ]}
          cells={materialCells}
          config={{ forceShow: false }}
          i18n={{ translate: translation }}
          onChange={({ data }) => setFormData(data)}
        />
      </Box>

      {isEditing && (
        <Box mt={2} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<FontAwesomeIcon icon={faTimes} />}
          >
            {t("form.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<FontAwesomeIcon icon={faSave} />}
          >
            {t("form.save", "Save")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AppointmentTimelineItem;
